import React from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Typography,
} from "@mui/material";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip,
} from "victory";
import { Analysis, User } from "../../../types";
import { FeedbackLoopColors } from "../Colors";
import CelebrationIcon from "@mui/icons-material/Celebration";
import ConstructionIcon from "@mui/icons-material/Construction";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";

function AlignmentBlock({
  opportunity,
  safeChartWidth,
  user,
}: {
  opportunity: {
    question: string;
    alignment: number;
    others?: number;
    self?: number;
  };
  safeChartWidth: number;
  user: User;
}) {
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        avatar={
          opportunity.alignment >= 1 ? (
            <ArrowCircleUpIcon sx={{ color: "var(--color-green)" }} />
          ) : (
            <ArrowCircleDownIcon sx={{ color: "var(--color-red)" }} />
          )
        }
        title={
          opportunity.alignment < 1
            ? `Potential weakness: ${opportunity.question}`
            : `Hidden Skill: ${opportunity.question}`
        }
        subheader={`Alignment: ${(100 * opportunity.alignment).toFixed(0)}%`}
      />
      <CardContent>
        <VictoryChart
          horizontal
          theme={VictoryTheme.material}
          height={150}
          width={safeChartWidth}
          domain={{ x: [0.5, 1.5], y: [0.5, 5.5] }}
          style={{ parent: { marginTop: -40 } }}
        >
          <VictoryAxis
            style={{
              axis: { stroke: "white" },
              axisLabel: { padding: 20 },
              grid: { stroke: "white" },
              ticks: { stroke: "none", size: 5 },
              tickLabels: { padding: 5, stroke: "white", fill: "white" },
            }}
            tickLabelComponent={<></>}
          />

          <VictoryAxis
            dependentAxis
            label="Rating"
            style={{
              axis: { stroke: "white" },
              axisLabel: { padding: 30 },
              grid: { stroke: "white" },
              ticks: { stroke: "white", size: 5 },
              tickLabels: { padding: 5, stroke: "white", fill: "white" },
            }}
          />

          <VictoryBar
            barWidth={20}
            colorScale={FeedbackLoopColors}
            key={user.id}
            labelComponent={
              <VictoryTooltip constrainToVisibleArea orientation={"top"} />
            }
            name="bar"
            data={[
              {
                x: opportunity.question,
                y: opportunity.others,
                label: `${
                  opportunity.question
                } (PEERS: ${opportunity.others?.toFixed(1)})`,
              },
            ]}
            style={{
              data: {
                stroke: "white",
                strokeDasharray: 0,
                strokeWidth: 2,
                fill: FeedbackLoopColors[0],
              },
            }}
          />

          <VictoryScatter
            key={user.id + "others"}
            style={{
              data: {
                fill: "white",
              },
            }}
            size={6}
            labelComponent={
              <VictoryTooltip constrainToVisibleArea orientation={"top"} />
            }
            name="scatter"
            data={[
              {
                x: opportunity.question,
                y: opportunity.self,
                label: `${
                  opportunity.question
                } (SELF: ${opportunity.self?.toFixed(1)})`,
                others: opportunity.others,
              },
            ]}
          />
        </VictoryChart>

        <Typography variant="caption" sx={{ mt: 2, ml: 4 }}>
          {opportunity.alignment >= 1
            ? `Your peers rated you ${(
                (opportunity.alignment - 1) *
                100
              ).toFixed(0)}% higher than you rated yourself.`
            : `Your peers rated you ${(
                -(opportunity.alignment - 1) * 100
              ).toFixed(0)}% lower than you rated yourself.`}
        </Typography>
      </CardContent>
    </Card>
  );
}

function InsightsHero({
  message,
  sentiment = "neutral",
}: {
  message: string;
  sentiment?: "positive" | "negative" | "neutral";
}) {
  return (
    <Paper sx={{ mb: 2, p: 2, bgcolor: "#1c1c1c", backgroundImage: "none" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        {sentiment === "positive" && (
          <CelebrationIcon sx={{ fontSize: 80, color: "green", mt: 2 }} />
        )}
        {sentiment === "negative" && (
          <ConstructionIcon sx={{ fontSize: 80, color: "gray", mt: 2 }} />
        )}
        {sentiment === "neutral" && (
          <ThumbUpIcon sx={{ fontSize: 80, color: "green", mt: 2 }} />
        )}

        <Typography variant="h6" sx={{ m: 2 }}>
          {message}
        </Typography>
      </Box>
    </Paper>
  );
}

export function OpportunitiesBlock({
  analyses,
  safeChartWidth,
  user,
}: {
  analyses: Analysis[];
  safeChartWidth: number;
  user: User;
}) {
  const normalizedAlignments = analyses
    .filter(
      (analysis) =>
        analysis && analysis.gap !== undefined && analysis.self !== undefined,
    )
    .map((analysis) => ({
      question: analysis.question.label,
      alignment: 1 + (analysis.gap as number) / (analysis.self as number),
      others: analysis.others,
      self: analysis.self,
    }));

  const filteredNegativeAlignments = normalizedAlignments.filter(
    (alignment) => alignment.alignment < 0.85,
  );
  const filteredPositiveAlignments = normalizedAlignments.filter(
    (alignment) => alignment.alignment > 1.15,
  );
  const bottom3NegativeAlignments = filteredNegativeAlignments
    .sort((a, b) => a.alignment - b.alignment)
    .slice(0, 3);
  const top3PositiveAlignments = filteredPositiveAlignments
    .sort((a, b) => b.alignment - a.alignment)
    .slice(0, 3);

  return (
    <>
      {top3PositiveAlignments.length === 0 &&
        bottom3NegativeAlignments.length === 0 && (
          <InsightsHero
            message={
              "Amazing! Your self ratings and received ratings were well aligned."
            }
            sentiment="neutral"
          />
        )}
      {top3PositiveAlignments.length > 0 && (
        <InsightsHero
          message={
            "Congratulations! Your peers have rated you more highly than you rated yourself across one or more facets, suggesting you have hidden skills."
          }
          sentiment="positive"
        />
      )}
      {top3PositiveAlignments.map((opportunity) => (
        <AlignmentBlock
          key={`positive-${opportunity.question}`}
          opportunity={opportunity}
          safeChartWidth={safeChartWidth}
          user={user}
        />
      ))}
      {bottom3NegativeAlignments.length > 0 && (
        <InsightsHero
          message={
            "Your peers have rated you lower than you rated yourself across one or more facets, suggesting there may be opportunities for improvement based on the results below."
          }
          sentiment="negative"
        />
      )}
      {bottom3NegativeAlignments.map((opportunity) => (
        <AlignmentBlock
          key={`negative-${opportunity.question}`}
          opportunity={opportunity}
          safeChartWidth={safeChartWidth}
          user={user}
        />
      ))}
    </>
  );
}
